import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner, Modal, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt, faPlus, faEdit, faTrash, faDownload, faSearch,
  faFilter, faSyncAlt, faFilePdf, faFileWord, faFileExcel,
  faFileImage, faFile, faCheckCircle, faExclamationTriangle,
  faCalendarAlt, faTimes, faSort, faSortUp, faSortDown,
  faLayerGroup, faFileUpload, faEye, faInfoCircle,
  faSave, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

// ============================================================
// 1. UTILITAIRE FETCH JSON SÉCURISÉ
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  console.log(`📡 Réponse brute :`, text.substring(0, 200));

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }

  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// 2. CACHE DEXIE (24h) avec fallback périmé
// ============================================================
const db = new Dexie('DocumentsCacheDB');
db.version(1).stores({
  documents: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedDocuments(key, allowExpired = false) {
  try {
    const entry = await db.documents.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}

async function setCachedDocuments(key, data) {
  await db.documents.put({ key, data, timestamp: Date.now() });
}

async function getStaleDocuments(key) {
  return getCachedDocuments(key, true);
}

// ============================================================
// 3. CONFIGURATION API
// ============================================================
const API_DOC = `${API_CONFIG2.apiUrl}document/api_document.php`;

// ============================================================
// 4. COMPOSANT PRINCIPAL – OPTIMISÉ
// ============================================================
const ManageDocuments = () => {
  // États principaux
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dataSource, setDataSource] = useState('cache');
  const [toast, setToast] = useState(null);

  // Pagination & filtres
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 12 });
  const [searchTerm, setSearchTerm] = useState('');
  const [filtreCategorie, setFiltreCategorie] = useState('');
  const [filtreType, setFiltreType] = useState('');
  const [sortField, setSortField] = useState('dateAjout');
  const [sortOrder, setSortOrder] = useState('desc');
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);

  // Modal form
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    nom: '',
    type: '',
    categorie: '',
    dateAjout: new Date().toISOString().slice(0, 10)
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Modal delete
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Refs
  const abortControllerRef = useRef(null);
  const isFetchingRef = useRef(false);          // ← sémaphore
  const debounceTimerRef = useRef(null);        // ← debounce unifié
  const toastTimeoutRef = useRef(null);

  // ============================================================
  // 5. GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((msg, variant = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ msg, variant });
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, 5000);
  }, []);

  // ============================================================
  // 6. CHARGEMENT DES FILTRES (une seule fois)
  // ============================================================
  const fetchFilterOptions = useCallback(async () => {
    try {
      const [catData, typeData] = await Promise.all([
        fetchJSON(`${API_DOC}?action=fetch_categories`),
        fetchJSON(`${API_DOC}?action=fetch_types`)
      ]);
      if (catData.status === 'success') setCategories(catData.data);
      if (typeData.status === 'success') setTypes(typeData.data);
    } catch (err) {
      console.error('Erreur chargement filtres', err);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // ============================================================
  // 7. RÉCUPÉRATION DES DOCUMENTS (avec cache, 429, sémaphore)
  // ============================================================
  const fetchDocuments = useCallback(async (page, forceRefresh = false, retryCount = 0) => {
    // Annuler l'ancienne requête
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Sémaphore : éviter les appels concurrents sauf forceRefresh
    if (!forceRefresh && isFetchingRef.current) return;
    isFetchingRef.current = true;

    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setDataSource('');

    const cacheKey = `page_${page}_search_${searchTerm}_cat_${filtreCategorie}_type_${filtreType}_sort_${sortField}_order_${sortOrder}`;

    try {
      // 1. Cache si non forcé
      if (!forceRefresh) {
        const cached = await getCachedDocuments(cacheKey);
        if (cached) {
          setDocuments(cached.data);
          setPagination(cached.pagination);
          setDataSource('cache');
          setLoading(false);
          isFetchingRef.current = false;
          return;
        }
      }

      // 2. Requête réseau
      const params = new URLSearchParams({
        action: 'fetch',
        page,
        limit: pagination.limit,
        search: searchTerm,
        categorie: filtreCategorie,
        type: filtreType,
        sort: sortField,
        order: sortOrder
      });
      const url = `${API_DOC}?${params.toString()}`;
      const response = await fetch(url, { signal: controller.signal });

      // 3. Gestion 429
      if (response.status === 429) {
        const stale = await getStaleDocuments(cacheKey);
        if (stale) {
          setDocuments(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          showToast('429 – Utilisation du cache (données peut-être obsolètes)', 'warning');
          setLoading(false);
          setRefreshing(false);
          isFetchingRef.current = false;
          return;
        }
        const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
        showToast(`Trop de requêtes. Nouvel essai dans ${waitTime / 1000}s`, 'warning');
        setTimeout(() => fetchDocuments(page, forceRefresh, retryCount + 1), waitTime);
        isFetchingRef.current = false;   // on relâche pour permettre le retry
        return;
      }

      // 4. Autres erreurs HTTP
      if (!response.ok) {
        const stale = await getStaleDocuments(cacheKey);
        if (stale) {
          setDocuments(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          showToast('Erreur réseau – Utilisation du cache', 'warning');
          setLoading(false);
          setRefreshing(false);
          isFetchingRef.current = false;
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      // 5. Lecture JSON
      const data = await fetchJSON(url, { signal: controller.signal });
      if (data.status === 'success') {
        const payload = { data: data.data, pagination: data.pagination };
        setDocuments(data.data);
        setPagination(data.pagination);
        const source = forceRefresh ? 'live (forcé)' : 'live';
        setDataSource(source);
        await setCachedDocuments(cacheKey, payload);
        if (forceRefresh) showToast('Données actualisées', 'success');
      } else {
        throw new Error(data.message || 'Erreur serveur');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('fetchDocuments error:', err);
      const stale = await getStaleDocuments(cacheKey);
      if (stale) {
        setDocuments(stale.data);
        setPagination(stale.pagination);
        setDataSource('cache (périmé)');
        showToast('Erreur – Utilisation du cache', 'warning');
      } else {
        showToast(err.message || 'Erreur de chargement', 'danger');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
      isFetchingRef.current = false;
    }
  }, [searchTerm, filtreCategorie, filtreType, sortField, sortOrder, pagination.limit, showToast]);

  // ============================================================
  // 8. DEBOUNCE UNIFIÉ POUR TOUS LES CHANGEMENTS DE FILTRES
  // ============================================================
  const debouncedFetch = useCallback((page = 1) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      fetchDocuments(page, false);
    }, 300); // 300ms de délai après la dernière modification
  }, [fetchDocuments]);

  // Appliquer le debounce dès qu'un filtre change
  useEffect(() => {
    debouncedFetch(1);
  }, [debouncedFetch]);  // debouncedFetch change si fetchDocuments change, mais ça suffit

  // Recherche : met à jour searchTerm (le debounce s'enclenche via l'effet précédent)
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // ============================================================
  // 9. ACTIONS : Actualiser, Forcer
  // ============================================================
  const handleRefresh = () => {
    if (!refreshing && !loading) {
      // Annule le debounce en cours pour exécuter immédiatement
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      fetchDocuments(pagination.currentPage, true);
    }
  };

  const handleRefreshForce = () => {
    if (!refreshing && !loading) {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      db.documents.clear();
      fetchDocuments(pagination.currentPage, true);
    }
  };

  // ============================================================
  // 10. GESTION DU TRI
  // ============================================================
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return <FontAwesomeIcon icon={faSort} className="text-muted opacity-50" />;
    return <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortUp : faSortDown} className="text-accent" />;
  };

  // ============================================================
  // 11. MODALE : OUVERTURE / FERMETURE / SUBMIT
  // ============================================================
  const resetForm = () => {
    setForm({ nom: '', type: '', categorie: '', dateAjout: new Date().toISOString().slice(0, 10) });
    setSelectedFile(null);
    setEditingId(null);
    setDragOver(false);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (doc) => {
    setForm({
      nom: doc.nom,
      type: doc.type,
      categorie: doc.categorie,
      dateAjout: doc.dateAjout
    });
    setEditingId(doc.id);
    setSelectedFile(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.type || !form.categorie) {
      showToast('Veuillez remplir tous les champs', 'warning');
      return;
    }
    if (!editingId && !selectedFile) {
      showToast('Veuillez sélectionner un fichier', 'warning');
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append('nom', form.nom);
    formData.append('type', form.type);
    formData.append('categorie', form.categorie);
    formData.append('dateAjout', form.dateAjout);
    if (selectedFile) formData.append('documentFile', selectedFile);

    try {
      let data;
      if (editingId) {
        data = await fetchJSON(`${API_DOC}?action=update&id=${editingId}`, {
          method: 'PUT',
          body: formData
        });
      } else {
        data = await fetchJSON(`${API_DOC}?action=create`, {
          method: 'POST',
          body: formData
        });
      }
      if (data.status === 'success') {
        showToast(editingId ? 'Document modifié' : 'Document ajouté', 'success');
        setShowModal(false);
        await db.documents.clear();
        // Rechargement immédiat (force)
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        fetchDocuments(pagination.currentPage, true);
      } else {
        showToast(data.message || 'Erreur', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Erreur réseau', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = (doc) => {
    window.open(`${API_DOC}?action=download&id=${doc.id}`, '_blank');
  };

  const confirmDelete = (doc) => {
    setDeleteTarget(doc);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const data = await fetchJSON(`${API_DOC}?action=delete&id=${deleteTarget.id}`, { method: 'DELETE' });
      if (data.status === 'success') {
        showToast('Document supprimé', 'success');
        setShowDeleteModal(false);
        await db.documents.clear();
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        fetchDocuments(pagination.currentPage, true);
      } else {
        showToast(data.message || 'Erreur', 'danger');
      }
    } catch (err) {
      showToast(err.message || 'Erreur réseau', 'danger');
    } finally {
      setDeleting(false);
    }
  };

  // ============================================================
  // 12. RENDU : FICHIER ICON MAPPING
  // ============================================================
  const getFileIcon = (type) => {
    const map = {
      pdf: { icon: faFilePdf, color: '#ef4444' },
      docx: { icon: faFileWord, color: '#3b82f6' },
      doc: { icon: faFileWord, color: '#3b82f6' },
      xlsx: { icon: faFileExcel, color: '#22c55e' },
      xls: { icon: faFileExcel, color: '#22c55e' },
      png: { icon: faFileImage, color: '#8b5cf6' },
      jpg: { icon: faFileImage, color: '#8b5cf6' },
      jpeg: { icon: faFileImage, color: '#8b5cf6' },
      gif: { icon: faFileImage, color: '#8b5cf6' },
    };
    return map[type?.toLowerCase()] || { icon: faFile, color: '#6b7280' };
  };

  // ============================================================
  // 13. PAGINATION (avec ellipses)
  // ============================================================
  const renderPagination = () => {
    const { currentPage, totalPages } = pagination;
    if (totalPages <= 1) return null;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 2) {
        pages.push(i);
      } else if (i === currentPage - 3 || i === currentPage + 3) {
        pages.push('...');
      }
    }
    return (
      <div className="pagination-wrapper">
        <button className="page-btn" disabled={currentPage <= 1} onClick={() => fetchDocuments(1)}>«</button>
        <button className="page-btn" disabled={currentPage <= 1} onClick={() => fetchDocuments(currentPage - 1)}>‹</button>
        {pages.map((p, idx) => (
          <button
            key={idx}
            className={`page-btn ${p === currentPage ? 'active' : ''}`}
            onClick={() => typeof p === 'number' && fetchDocuments(p)}
            disabled={p === '...'}
          >
            {p}
          </button>
        ))}
        <button className="page-btn" disabled={currentPage >= totalPages} onClick={() => fetchDocuments(currentPage + 1)}>›</button>
        <button className="page-btn" disabled={currentPage >= totalPages} onClick={() => fetchDocuments(totalPages)}>»</button>
      </div>
    );
  };

  // ============================================================
  // 14. SKELETON ROW
  // ============================================================
  const SkeletonRow = () => (
    <div className="skeleton-row">
      <div className="skeleton-icon"></div>
      <div className="skeleton-text w-25"></div>
      <div className="skeleton-text w-20"></div>
      <div className="skeleton-text w-20"></div>
      <div className="skeleton-text w-15"></div>
      <div className="skeleton-text w-15"></div>
    </div>
  );

  // ============================================================
  // 15. AFFICHAGE DU CHARGEMENT INITIAL
  // ============================================================
  if (loading && documents.length === 0 && !refreshing) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement des documents...</p>
      </div>
    );
  }

  // ============================================================
  // 16. RENDU PRINCIPAL
  // ============================================================
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – MANAGE DOCUMENTS
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .doc-container {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: 30px;
          color: var(--text-primary);
          font-family: 'Inter', system-ui, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 1.5rem;
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .badge-source {
          font-size: 11px;
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 10px 20px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: 0.9rem;
          white-space: nowrap;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }
        .monetra-btn:disabled {
          opacity: 0.4;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          background: rgba(239, 68, 68, 0.12);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.2);
        }
        .monetra-btn.outline {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.08);
        }
        .monetra-btn.outline:hover:not(:disabled) {
          background: rgba(255,255,255,0.04);
        }

        .search-box {
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 20px;
          transition: all 0.3s;
        }
        .search-box:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
        }
        .search-box input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          flex: 1;
          padding: 8px 0;
          font-size: 0.95rem;
        }
        .search-box input::placeholder {
          color: var(--text-muted);
        }

        .filter-select {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 30px;
          color: var(--text-primary);
          padding: 8px 18px;
          outline: none;
          font-size: 0.9rem;
          appearance: none;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 16px;
          cursor: pointer;
        }
        .filter-select:focus {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
        }
        .filter-select option {
          background: #0b1428;
          color: var(--text-primary);
        }

        /* Tableau desktop (masqué en mobile) */
        .doc-table {
          width: 100%;
          border-collapse: collapse;
        }
        .doc-table th {
          color: var(--accent-blue);
          text-align: left;
          padding: 14px 12px;
          border-bottom: 1px solid rgba(255,255,255,0.04);
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 600;
          cursor: pointer;
          user-select: none;
          transition: color 0.2s;
        }
        .doc-table th:hover {
          color: var(--text-primary);
        }
        .doc-table td {
          padding: 14px 12px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
        }
        .doc-table tr:hover td {
          background: rgba(255,255,255,0.02);
        }

        .file-icon-wrapper {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          border-radius: 10px;
          font-size: 1.1rem;
          background: rgba(255,255,255,0.04);
        }

        /* Cartes mobiles (masquées en desktop) */
        .mobile-cards {
          display: none;
          flex-direction: column;
          gap: 12px;
          padding: 1rem 0;
        }
        .doc-card-mobile {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.04);
          border-radius: 16px;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .doc-card-mobile .card-header {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .doc-card-mobile .file-icon {
          font-size: 2rem;
          width: 40px;
          text-align: center;
        }
        .doc-card-mobile .doc-name {
          font-weight: 600;
          font-size: 1rem;
          color: var(--text-primary);
        }
        .doc-card-mobile .doc-meta {
          font-size: 0.8rem;
          color: var(--text-secondary);
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .doc-card-mobile .card-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }
        .doc-card-mobile .card-actions .monetra-btn {
          padding: 8px 12px;
          font-size: 0.85rem;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .doc-container {
            padding: 16px;
          }
          .doc-table {
            display: none; /* on cache le tableau */
          }
          .mobile-cards {
            display: flex; /* on affiche les cartes */
          }
          .doc-card-mobile .doc-name {
            font-size: 0.95rem;
          }
          .doc-card-mobile .doc-meta {
            font-size: 0.75rem;
          }
          .doc-card-mobile .card-actions .monetra-btn {
            padding: 10px 14px;
            font-size: 0.9rem;
          }
          .monetra-btn {
            font-size: 0.85rem;
          }
          .search-box input {
            font-size: 0.9rem;
          }
          .filter-select {
            font-size: 0.85rem;
            padding: 6px 14px;
          }
          h1.display-6 {
            font-size: 1.6rem;
          }
        }

        @media (max-width: 576px) {
          .doc-container {
            padding: 10px;
          }
          .glass-card {
            padding: 1rem;
            border-radius: 12px;
          }
          .monetra-btn {
            padding: 8px 14px;
            font-size: 0.8rem;
          }
          .doc-card-mobile .doc-name {
            font-size: 0.9rem;
          }
          .doc-card-mobile .doc-meta {
            font-size: 0.7rem;
          }
          .doc-card-mobile .card-actions .monetra-btn {
            padding: 8px 12px;
            font-size: 0.85rem;
          }
          .pagination-wrapper .page-btn {
            padding: 4px 10px;
            font-size: 0.75rem;
          }
        }

        .toast-container {
          position: fixed;
          top: 24px;
          right: 24px;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.8);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: var(--radius);
          padding: 14px 20px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          animation: slideIn 0.35s ease;
          position: relative;
          overflow: hidden;
        }
        .toast-item.success {
          border-left: 4px solid var(--accent-blue);
        }
        .toast-item.danger {
          border-left: 4px solid #ef4444;
        }
        .toast-item.warning {
          border-left: 4px solid #f59e0b;
        }
        .toast-progress {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          background: currentColor;
          animation: progressShrink 5s linear forwards;
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes progressShrink {
          from { width: 100%; }
          to { width: 0%; }
        }

        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover {
          color: var(--text-primary);
        }

        /* Modal glass */
        .modal-glass .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
          overflow: hidden;
        }
        .modal-glass .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
          padding: 1.5rem 2rem;
          background: rgba(0,0,0,0.15);
        }
        .modal-glass .modal-header .modal-title {
          font-weight: 600;
          font-size: 1.3rem;
        }
        .modal-glass .modal-body {
          padding: 2rem;
        }
        .modal-glass .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
          padding: 1.2rem 2rem;
          background: rgba(0,0,0,0.08);
        }
        .modal-glass .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }
        .modal-glass .btn-close:hover {
          opacity: 1;
        }

        .form-label {
          font-size: 0.85rem;
          font-weight: 500;
          color: var(--text-secondary);
          margin-bottom: 4px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .form-label .required {
          color: #f87171;
          margin-left: 2px;
        }

        .input-glass {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          padding: 10px 16px;
          color: var(--text-primary);
          width: 100%;
          transition: all 0.25s;
          font-size: 0.95rem;
        }
        .input-glass:focus {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
          outline: none;
        }
        .input-glass::placeholder {
          color: var(--text-muted);
        }

        .file-drop-zone {
          border: 2px dashed rgba(255,255,255,0.06);
          border-radius: 16px;
          padding: 2rem;
          text-align: center;
          transition: all 0.3s;
          cursor: pointer;
        }
        .file-drop-zone.drag-over {
          border-color: var(--accent-blue);
          background: rgba(59, 130, 246, 0.08);
        }
        .file-drop-zone .upload-icon {
          font-size: 3rem;
          color: var(--text-muted);
          margin-bottom: 8px;
        }
        .file-drop-zone .upload-text {
          color: var(--text-secondary);
          font-weight: 500;
        }
        .file-drop-zone .upload-hint {
          color: var(--text-muted);
          font-size: 0.85rem;
        }
        .file-drop-zone .file-name {
          color: var(--accent-blue);
          font-weight: 500;
        }

        .pagination-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 6px;
          margin-top: 1.5rem;
          flex-wrap: wrap;
        }
        .page-btn {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          color: var(--text-primary);
          padding: 6px 14px;
          font-size: 0.85rem;
          transition: all 0.2s;
          cursor: pointer;
          min-width: 36px;
          text-align: center;
        }
        .page-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
        }
        .page-btn.active {
          background: var(--accent-blue);
          color: #fff;
          border-color: var(--accent-blue);
          font-weight: 600;
        }
        .page-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .skeleton-row {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 12px 0;
          border-bottom: 1px solid rgba(255,255,255,0.02);
        }
        .skeleton-icon {
          width: 34px;
          height: 34px;
          border-radius: 10px;
          background: linear-gradient(90deg, #1a2a4a 25%, #2a4a7a 50%, #1a2a4a 75%);
          background-size: 200px 100%;
          animation: shimmer 1.5s infinite;
        }
        .skeleton-text {
          height: 14px;
          border-radius: 8px;
          background: linear-gradient(90deg, #1a2a4a 25%, #2a4a7a 50%, #1a2a4a 75%);
          background-size: 200px 100%;
          animation: shimmer 1.5s infinite;
        }
        .w-25 { width: 25%; }
        .w-20 { width: 20%; }
        .w-15 { width: 15%; }
        .w-10 { width: 10%; }

        @keyframes shimmer {
          0% { background-position: -200px 0; }
          100% { background-position: calc(200px + 100%) 0; }
        }

        .text-accent { color: var(--accent-blue); }
        .text-muted { color: var(--text-muted); }
        .text-secondary { color: var(--text-secondary); }
        .text-success { color: var(--accent-blue); }
        .text-danger { color: #ef4444; }
        .opacity-30 { opacity: 0.3; }
        .fw-bold { font-weight: 600; }
        .display-6 { font-size: 2rem; font-weight: 700; }
        .mb-1 { margin-bottom: 0.25rem; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-2 { margin-right: 0.5rem; }
        .mt-3 { margin-top: 1rem; }
        .py-4 { padding-top: 1.5rem; padding-bottom: 1.5rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .text-center { text-align: center; }
        .badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 50px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .bg-dark {
          background: rgba(255,255,255,0.04);
        }
        .text-white {
          color: var(--text-primary);
        }
        .border-0 { border: none; }
        .px-3 { padding-left: 1rem; padding-right: 1rem; }
        .py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
      `}</style>

      <div className="doc-container">
        {/* Header avec badge source et boutons */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <div>
            <h1 className="display-6 fw-bold mb-1">
              <FontAwesomeIcon icon={faFileAlt} className="text-success me-2" />
              Gestion des documents
            </h1>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <p className="text-secondary m-0">
                {pagination.totalRecords} document(s) · {pagination.totalPages} page(s)
              </p>
              <span className={`badge-source ${dataSource === 'live' || dataSource === 'live (forcé)' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
                {dataSource === 'cache' ? '📦 Cache' :
                 dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
                 dataSource === 'live' ? '🔄 Live' :
                 dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
              </span>
            </div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button className="monetra-btn outline" onClick={handleRefresh} disabled={refreshing || loading}>
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} /> Actualiser
            </button>
            <button className="monetra-btn outline" onClick={handleRefreshForce} disabled={refreshing || loading}>
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} style={{ color: '#ef4444' }} /> Forcer
            </button>
            <button className="monetra-btn primary" onClick={openCreateModal}>
              <FontAwesomeIcon icon={faPlus} /> Ajouter
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="glass-card mb-4">
          <div className="row g-3 align-items-center">
            <div className="col-md-4">
              <div className="search-box">
                <FontAwesomeIcon icon={faSearch} className="text-muted" />
                <input
                  type="text"
                  placeholder="Rechercher un document..."
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </div>
            </div>
            <div className="col-md-3">
              <select
                className="filter-select w-100"
                value={filtreCategorie}
                onChange={e => setFiltreCategorie(e.target.value)}
              >
                <option value="">Toutes catégories</option>
                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="filter-select w-100"
                value={filtreType}
                onChange={e => setFiltreType(e.target.value)}
              >
                <option value="">Tous types</option>
                {types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="col-md-2 text-end">
              <button className="monetra-btn outline" onClick={() => fetchDocuments(pagination.currentPage, false)} disabled={loading}>
                <FontAwesomeIcon icon={faSyncAlt} spin={loading} /> Rafraîchir
              </button>
            </div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="glass-card p-0 overflow-hidden">
          {/* Tableau desktop */}
          <div className="doc-table-wrapper">
            <table className="doc-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('nom')}>
                    Nom {renderSortIcon('nom')}
                  </th>
                  <th onClick={() => handleSort('type')}>
                    Type {renderSortIcon('type')}
                  </th>
                  <th onClick={() => handleSort('categorie')}>
                    Catégorie {renderSortIcon('categorie')}
                  </th>
                  <th onClick={() => handleSort('taille')}>
                    Taille {renderSortIcon('taille')}
                  </th>
                  <th onClick={() => handleSort('dateAjout')}>
                    Date {renderSortIcon('dateAjout')}
                  </th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && documents.length === 0 ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}><td colSpan="6"><SkeletonRow /></td></tr>
                  ))
                ) : documents.length === 0 ? (
                  <tr><td colSpan="6" className="text-center text-muted py-5">
                    <FontAwesomeIcon icon={faFileAlt} size="3x" className="opacity-30 mb-3" />
                    <p>Aucun document trouvé</p>
                  </td></tr>
                ) : (
                  documents.map(doc => {
                    const fileInfo = getFileIcon(doc.type);
                    return (
                      <tr key={doc.id}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <span className="file-icon-wrapper" style={{ color: fileInfo.color, background: `${fileInfo.color}15` }}>
                              <FontAwesomeIcon icon={fileInfo.icon} />
                            </span>
                            <span>{doc.nom}</span>
                          </div>
                        </td>
                        <td><span className="badge bg-dark text-white border-0 px-3 py-1 rounded-pill">{doc.type}</span></td>
                        <td>{doc.categorie}</td>
                        <td>{doc.taille}</td>
                        <td>{doc.dateAjout}</td>
                        <td>
                          <div className="d-flex gap-2 justify-content-center">
                            <button className="monetra-btn outline" onClick={() => handleDownload(doc)} title="Télécharger">
                              <FontAwesomeIcon icon={faDownload} />
                            </button>
                            <button className="monetra-btn outline" onClick={() => openEditModal(doc)} title="Modifier">
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button className="monetra-btn danger" onClick={() => confirmDelete(doc)} title="Supprimer">
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Cartes mobiles (affichées uniquement en responsive) */}
          <div className="mobile-cards">
            {loading && documents.length === 0 ? (
              [...Array(3)].map((_, i) => (
                <div key={i} className="skeleton-row" style={{ padding: '1rem' }}>
                  <div className="skeleton-icon"></div>
                  <div className="skeleton-text w-50"></div>
                </div>
              ))
            ) : documents.length === 0 ? (
              <p className="text-muted text-center py-4">Aucun document</p>
            ) : (
              documents.map(doc => {
                const fileInfo = getFileIcon(doc.type);
                return (
                  <div key={doc.id} className="doc-card-mobile">
                    <div className="card-header">
                      <FontAwesomeIcon icon={fileInfo.icon} className="file-icon" style={{ color: fileInfo.color }} />
                      <div>
                        <div className="doc-name">{doc.nom}</div>
                        <div className="doc-meta">
                          <span>{doc.categorie}</span>
                          <span>·</span>
                          <span>{doc.type}</span>
                          <span>·</span>
                          <span>{doc.taille}</span>
                        </div>
                      </div>
                    </div>
                    <div className="card-actions">
                      <button className="monetra-btn outline" onClick={() => handleDownload(doc)}>
                        <FontAwesomeIcon icon={faDownload} /> Télécharger
                      </button>
                      <button className="monetra-btn outline" onClick={() => openEditModal(doc)}>
                        <FontAwesomeIcon icon={faEdit} /> Modifier
                      </button>
                      <button className="monetra-btn danger" onClick={() => confirmDelete(doc)}>
                        <FontAwesomeIcon icon={faTrash} /> Supprimer
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {renderPagination()}
        </div>
      </div>

      {/* ============================================================
          MODAL CREATE / EDIT
          ============================================================ */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered backdrop="static" className="modal-glass" size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={editingId ? faEdit : faPlus} className="text-success me-2" />
            {editingId ? 'Modifier le document' : 'Nouveau document'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="form-label">
                <FontAwesomeIcon icon={faFileAlt} className="text-muted" /> Nom du document <span className="required">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={form.nom}
                onChange={e => setForm({...form, nom: e.target.value})}
                className="input-glass"
                placeholder="Ex: Contrat de travail, Facture..."
                required
                disabled={submitting}
              />
            </Form.Group>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label className="form-label">
                  <FontAwesomeIcon icon={faLayerGroup} className="text-muted" /> Type <span className="required">*</span>
                </Form.Label>
                <Form.Select
                  value={form.type}
                  onChange={e => setForm({...form, type: e.target.value})}
                  className="input-glass"
                  required
                  disabled={submitting}
                >
                  <option value="">Sélectionner</option>
                  <option value="PDF">PDF</option>
                  <option value="DOCX">DOCX</option>
                  <option value="XLSX">XLSX</option>
                  <option value="JPG">JPG</option>
                  <option value="PNG">PNG</option>
                  <option value="Autre">Autre</option>
                </Form.Select>
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label className="form-label">
                  <FontAwesomeIcon icon={faLayerGroup} className="text-muted" /> Catégorie <span className="required">*</span>
                </Form.Label>
                <Form.Select
                  value={form.categorie}
                  onChange={e => setForm({...form, categorie: e.target.value})}
                  className="input-glass"
                  required
                  disabled={submitting}
                >
                  <option value="">Sélectionner</option>
                  <option value="Administratif">Administratif</option>
                  <option value="RH">RH</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Technique">Technique</option>
                  <option value="Autre">Autre</option>
                </Form.Select>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label className="form-label">
                <FontAwesomeIcon icon={faFileUpload} className="text-muted" />
                Fichier {!editingId && <span className="required">*</span>}
              </Form.Label>
              <div
                className={`file-drop-zone ${dragOver ? 'drag-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  if (e.dataTransfer.files.length) {
                    setSelectedFile(e.dataTransfer.files[0]);
                  }
                }}
                onClick={() => document.getElementById('fileInput').click()}
              >
                {selectedFile ? (
                  <>
                    <FontAwesomeIcon icon={faFileAlt} className="upload-icon" style={{ color: 'var(--accent-blue)' }} />
                    <div className="file-name">{selectedFile.name}</div>
                    <div className="upload-hint">({(selectedFile.size / 1024).toFixed(1)} KB)</div>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faFileUpload} className="upload-icon" />
                    <div className="upload-text">Cliquez ou glissez-déposez</div>
                    <div className="upload-hint">PDF, DOCX, XLSX, images... (max 10 Mo)</div>
                  </>
                )}
                <input
                  id="fileInput"
                  type="file"
                  style={{ display: 'none' }}
                  onChange={e => {
                    if (e.target.files.length) setSelectedFile(e.target.files[0]);
                  }}
                />
              </div>
              {editingId && !selectedFile && (
                <small className="text-muted">Laisser vide pour conserver le fichier actuel</small>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="form-label">
                <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" /> Date
              </Form.Label>
              <Form.Control
                type="date"
                value={form.dateAjout}
                onChange={e => setForm({...form, dateAjout: e.target.value})}
                className="input-glass"
                disabled={submitting}
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button type="button" className="monetra-btn outline" onClick={() => setShowModal(false)} disabled={submitting}>
                <FontAwesomeIcon icon={faTimes} /> Annuler
              </button>
              <button type="submit" className="monetra-btn primary" disabled={submitting}>
                {submitting ? <Spinner as="span" animation="border" size="sm" /> : <FontAwesomeIcon icon={editingId ? faSave : faPlus} />}
                {editingId ? 'Mettre à jour' : 'Ajouter'}
              </button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ============================================================
          MODAL DELETE
          ============================================================ */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered backdrop="static" className="modal-glass" size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <FontAwesomeIcon icon={faTrash} className="me-2" /> Supprimer
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Supprimer définitivement <strong>« {deleteTarget?.nom} »</strong> ?</p>
          <small className="text-muted">Cette action est irréversible.</small>
        </Modal.Body>
        <Modal.Footer>
          <button className="monetra-btn outline" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Annuler
          </button>
          <button className="monetra-btn danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Spinner as="span" animation="border" size="sm" /> : <FontAwesomeIcon icon={faTrash} />}
            Supprimer
          </button>
        </Modal.Footer>
      </Modal>

      {/* TOAST */}
      {toast && (
        <div className="toast-container">
          <div className={`toast-item ${toast.variant}`}>
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
            />
            <span>{toast.msg}</span>
            <button className="btn-close-custom" onClick={() => setToast(null)}>✕</button>
            <div className="toast-progress" style={{ color: toast.variant === 'success' ? 'var(--accent-blue)' : '#f87171' }} />
          </div>
        </div>
      )}
    </>
  );
};

export default ManageDocuments;